import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { TRUSTPILOT_RATING_COUNT, TRUSTPILOT_RATING_VALUE } from '@/config';
import { useMedia } from '@/hooks/responsive';
import { Carousel, CarouselContent, CarouselItem } from '@/components/Carousel';
import { Box, Container, Stack, Typography } from '@mui/material';

import Rating from './Rating';
import { REVIEWS_NAMES } from './constants';

function formatDate(timestamp) {
	return dayjs.unix(timestamp).format('MMMM D, YYYY');
}

function GlobalRating() {
	const mdDown = useMedia('mdDown');
	const { t } = useTranslation();

	const { i18n } = useTranslation();

	function formatValue(value) {
		return new Intl.NumberFormat(i18n.language).format(value);
	}

	return (
		<Box
			sx={{
				border: '1px solid #E8E8E8',
				display: 'flex',
				width: 'fit-content',
				alignItems: 'center',
				borderRadius: '8px',
				padding: '12px',
				gap: '16px'
			}}
		>
			<Rating starSize={mdDown ? 16 : 20} value={TRUSTPILOT_RATING_VALUE} />
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center'
				}}
			>
				<Typography variant={mdDown ? 'body0' : 'body1'} fontWeight={700}>
					{t('fastCheckout.reviews.score', {
						value: TRUSTPILOT_RATING_VALUE.toFixed(1)
					})}
				</Typography>
				<Box component="span" color="text.secondary" sx={{ mx: '4px' }}>
					|
				</Box>
				<Typography variant="body0" color="text.secondary">
					{t('fastCheckout.reviews.quantity', {
						quantity: formatValue(TRUSTPILOT_RATING_COUNT)
					})}
				</Typography>
			</Box>
		</Box>
	);
}

function ReviewBox({ title, description, name, date }) {
	return (
		<Stack sx={{ width: '100%', gap: '9px' }}>
			<Typography variant="body1" fontWeight={500} color="black">
				{title}
			</Typography>
			<Rating starSize={12} value={TRUSTPILOT_RATING_VALUE} />
			<Typography variant="body0" color="text.secondary">
				{description}
			</Typography>
			<Typography
				variant="body0"
				color="text.secondary"
				sx={{
					display: 'flex',
					flex: 1,
					alignItems: 'center',
					gap: '8px',
					fontWeight: 700
				}}
			>
				<Box component="span">{name}</Box>
				<Box
					component="span"
					sx={({ palette }) => ({
						background: palette.text.secondary,
						height: '2px',
						width: '2px',
						borderRadius: '100%'
					})}
				/>
				<Box component="span">{formatDate(date)}</Box>
			</Typography>
		</Stack>
	);
}

function Reviews() {
	const { t } = useTranslation();
	const mdDown = useMedia('mdDown');

	const [api, setApi] = useState();
	const [showLeftFade, setShowLeftFade] = useState(false);
	const [showRightFade, setShowRightFade] = useState(false);

	const reviews = useMemo(
		() =>
			REVIEWS_NAMES.map((name, index) => ({
				name,
				title: t(`fastCheckout.reviews.items.${index}.title`),
				description: t(`fastCheckout.reviews.items.${index}.description`),
				date: dayjs()
					.subtract(index + 1, 'day')
					.unix()
			})),
		[t, dayjs]
	);

	useEffect(() => {
		if (!api) return;

		const handleScroll = () => {
			setShowLeftFade(api.canScrollPrev());
			setShowRightFade(api.canScrollNext());
		};

		handleScroll();

		api.on('scroll', handleScroll);
		api.on('reInit', handleScroll);

		return () => {
			api.off('scroll', handleScroll);
			api.off('reInit', handleScroll);
		};
	}, [api]);

	return (
		<Box
			component="section"
			sx={{
				mx: 'auto',
				display: 'flex',
				width: '100%',
				maxWidth: '1024px',
				flexDirection: 'column',
				gap: '28px'
			}}
		>
			<Container
				maxWidth="false"
				sx={{
					display: 'flex',
					width: '100%',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: '16px',
					px: { xs: '20px', md: 0 }
				}}
			>
				<Stack sx={{ gap: '4px' }}>
					<Typography variant={mdDown ? 'body3' : 'h3'}>
						{t('fastCheckout.reviews.title')}
					</Typography>
					<Typography variant="body0" color="text.secondary">
						{t('fastCheckout.reviews.description')}
					</Typography>
				</Stack>
				<GlobalRating />
			</Container>
			<Box
				sx={{
					position: 'relative',
					minWidth: 0,
					pl: { xs: '20px', md: 0 },
					userSelect: 'none'
				}}
			>
				<Carousel
					setApi={setApi}
					opts={{
						align: 'start',
						dragFree: true
					}}
				>
					<CarouselContent>
						{reviews.map((reviewItem, index) => (
							<CarouselItem
								key={index}
								sx={{
									display: 'flex',
									flexBasis: '260px'
								}}
							>
								<ReviewBox {...reviewItem} />
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>

				{showLeftFade && (
					<Box
						aria-hidden
						sx={{
							pointerEvents: 'none',
							position: 'absolute',
							top: 0,
							left: 0,
							zIndex: 10,
							height: '100%',
							width: '20px',
							background: 'linear-gradient(to right, white, transparent)'
						}}
					/>
				)}

				{showRightFade && (
					<Box
						aria-hidden
						sx={{
							pointerEvents: 'none',
							position: 'absolute',
							top: 0,
							right: 0,
							zIndex: 10,
							height: '100%',
							width: '40px',
							background: 'linear-gradient(to left, white, transparent)'
						}}
					/>
				)}
			</Box>
		</Box>
	);
}

export default Reviews;
